import { CopyButton, Row } from "@gadgetinc/widgets";
import { ChangeLanguageIcon } from "@gadgetinc/widgets/src/icons/ChangeLanguageIcon";
import { useStyletron } from "baseui";
import { Button, KIND as ButtonKind, SIZE } from "baseui/button";
import { StatefulMenu } from "baseui/menu";
import { PLACEMENT, StatefulPopover } from "baseui/popover";
import { defaultTo, isString, mapValues, memoize, sortBy } from "lodash";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EsbuildTranspiler } from "shared-utils/src/ts-converter/EsbuildTranspiler";
import { DevelopmentEnvironmentName } from "state-trees/src/Environment";
import { prettifyCodeExample } from "state-trees/src/prettifyCodeExample";
import { CodeBlockFilename, CodeBlockTitle, CodeBlockTitleBar, CodeBlockTitleBarSpacer } from "web/src/components/markdown/CodeBlock";
import { CodeHighlight } from "web/src/components/markdown/CodeHighlight";
import { LanguageIcon, LanguageIndicator, languageLabel } from "web/src/components/markdown/LanguageIndicator";
import { DocsContext } from "../chrome/DocsContext";
import { CodeBlock, CodeBlockContainer, handleCopyWithToast } from "./CodeBlock";
import { renameTsToJs } from "./InlineTsFilePath";
import { OpenInGraphQLPlaygroundButton, OpenInJavaScriptPlaygroundButton } from "./OpenInPlaygroundButton";

type LanguageExampleMap = {
  [language: string]: string | { query: string; variables?: any };
};

const memoizedTransform = memoize(
  async (options: { code: string }) => {
    const transpiler = new EsbuildTranspiler();
    return await transpiler.transpile(options.code, true, {
      addActionTypeComments: true,
      addRouteTypeComment: true,
    });
  },
  (options) => JSON.stringify(options)
);

const memoizedMaybePrettify = memoize(
  (options: { code: string; language: string; prettify?: boolean }) => {
    const { code, language, prettify } = options;
    return defaultTo(prettify, true) ? prettifyCodeExample(code, language, 85) : code;
  },
  (options) => JSON.stringify(options)
);

const useLanguageCodeSnippets = (examples: LanguageExampleMap, prettify?: boolean, disableTranspiler?: boolean) => {
  const { initializedTranspiler } = useContext(DocsContext);

  const [languageCodeSnippets, setLanguageCodeSnippets] = useState(() => {
    const prettifiedExamples = mapValues(examples, (example, language) =>
      isString(example)
        ? memoizedMaybePrettify({ code: example, language, prettify })
        : {
            query: memoizedMaybePrettify({ code: example.query, language: "graphql", prettify }),
            variables: example.variables,
          }
    );

    if (Object.keys(prettifiedExamples).includes("typescript") && !Object.keys(prettifiedExamples).includes("javascript")) {
      prettifiedExamples.javascript = prettifiedExamples.typescript;
    }
    if (Object.keys(prettifiedExamples).includes("tsx") && !Object.keys(prettifiedExamples).includes("react")) {
      prettifiedExamples.react = prettifiedExamples.tsx;
    }

    return prettifiedExamples;
  });

  const [jsExample, setJsExample] = useState<string | undefined>(undefined);
  const [reactExample, setReactExample] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (initializedTranspiler && examples["typescript"]) {
      if (!disableTranspiler) {
        memoizedTransform({ code: examples["typescript"] as string })
          .then((result) => {
            setJsExample(result);
          })
          .catch(console.error);
      } else {
        setJsExample(examples["typescript"] as string);
      }
    }
    if (initializedTranspiler && examples["tsx"]) {
      if (!disableTranspiler) {
        memoizedTransform({ code: examples["tsx"] as string })
          .then((result) => {
            setReactExample(result);
          })
          .catch(console.error);
      } else {
        setReactExample(examples["tsx"] as string);
      }
    }
  }, [initializedTranspiler, examples, disableTranspiler]);

  useEffect(() => {
    setLanguageCodeSnippets((prev) => ({
      ...prev,
      ...(jsExample ? { javascript: memoizedMaybePrettify({ code: jsExample, language: "javascript", prettify }) } : {}),
      ...(reactExample ? { react: memoizedMaybePrettify({ code: reactExample, language: "react", prettify }) } : {}),
    }));
  }, [jsExample, prettify, reactExample]);

  return { initializedTranspiler, languageCodeSnippets };
};

const isJavaScriptLanguage = (language: string) => {
  return language === "javascript" || language === "typescript";
};

const isReactLanguage = (language: string) => {
  return language === "react" || language === "tsx" || language === "jsx";
};

const isTypeScriptRelatedLanguage = (language: string) => {
  return language === "typescript" || language === "tsx";
};

const getAvailableLanguages = (languageCodeSnippets: LanguageExampleMap, showTsLanguage: boolean) => {
  const languages = Object.keys(languageCodeSnippets);
  let filteredLanguages = languages;

  if (languages.some(isTypeScriptRelatedLanguage)) {
    // Only show ts and tsx if showTsLanguage is true
    filteredLanguages = filteredLanguages.filter((lang) => {
      const isJsOrReact = isJavaScriptLanguage(lang) || isReactLanguage(lang);
      if (!isJsOrReact) return true;
      return showTsLanguage ? isTypeScriptRelatedLanguage(lang) : !isTypeScriptRelatedLanguage(lang);
    });
  } else {
    // Since we don't have typescript or tsx, we show all languages
    filteredLanguages = languages;
  }

  // Sort languages according to priority
  return sortBy(filteredLanguages, [
    (lang) => {
      if (isJavaScriptLanguage(lang)) return 0;
      if (isReactLanguage(lang)) return 1;
      return 2;
    },
  ]);
};

/**
 * `TabbedCodeExample` is a component that renders multiple code examples with a transpiler.
 * If you want to render a single-language code example, use `TranspiledCodeExample` instead for a simpler API.
 */
export const TabbedCodeExample = observer(
  (props: {
    title?: ReactNode;
    filename?: string;
    examples: LanguageExampleMap;
    detachSelector?: boolean;
    showGqlPlaygroundLink?: boolean;
    showJsPlaygroundLink?: boolean;
    /** Set by the remark plugin when this tabbed example is auto-generated from a typescript code block */
    isAutoSelector?: boolean;
    prettify?: boolean;
    disableTranspiler?: boolean;
  }) => {
    const [css, $theme] = useStyletron();
    const { currentApp, showTsLanguage } = useContext(DocsContext);

    const { languageCodeSnippets } = useLanguageCodeSnippets(props.examples, props.prettify, props.disableTranspiler);
    /**
     * Filter the languages to only include the ones that are available based on the showTsLanguage flag
     */
    const availableLanguages = useMemo(
      () => getAvailableLanguages(languageCodeSnippets, showTsLanguage),
      [languageCodeSnippets, showTsLanguage]
    );

    const [selectedLanguage, setSelectedLanguage] = useState(availableLanguages[0]);
    // If the selected language is not available, set it to the first available language
    useEffect(() => {
      if (availableLanguages.length > 0 && !availableLanguages.includes(selectedLanguage)) {
        setSelectedLanguage(availableLanguages[0]);
      }
    }, [availableLanguages, selectedLanguage]);

    const shouldShowMenu = availableLanguages.length > 1;

    const filename = props.filename ? renameTsToJs(props.filename, showTsLanguage) : undefined;

    const menuItems = availableLanguages.map((language) => ({
      label: language,
      onClick: () => setSelectedLanguage(language),
    }));

    const copyCode = useCallback(() => {
      if (!selectedLanguage) return;

      const value = languageCodeSnippets[selectedLanguage];
      let code;
      if (isString(value)) {
        code = value;
      } else {
        code = value.query;
      }

      void handleCopyWithToast(code);
    }, [selectedLanguage, languageCodeSnippets]);

    let codeForDocsContentExtraction: string | undefined;
    let languageForDocsContentExtraction: string | undefined;
    if (languageCodeSnippets["typescript"]) {
      codeForDocsContentExtraction = languageCodeSnippets["typescript"] as string;
      languageForDocsContentExtraction = "typescript";
    } else if (languageCodeSnippets["javascript"]) {
      codeForDocsContentExtraction = languageCodeSnippets["javascript"] as string;
      languageForDocsContentExtraction = "javascript";
    }

    return (
      <CodeBlockContainer rawCode={codeForDocsContentExtraction} language={languageForDocsContentExtraction}>
        <CodeBlockTitleBar>
          {props.title && <CodeBlockTitle>{props.title}</CodeBlockTitle>}
          {filename && <CodeBlockFilename>{filename}</CodeBlockFilename>}
          <CodeBlockTitleBarSpacer />
          {shouldShowMenu ? (
            <StatefulPopover
              placement={PLACEMENT.bottom}
              overrides={{
                Body: {
                  style: {
                    backgroundColor: $theme.colors.primary600,
                    marginLeft: $theme.sizing.scale500,
                    marginRight: $theme.sizing.scale500,
                    marginTop: $theme.sizing.scale500,
                    marginBottom: $theme.sizing.scale500,
                    borderRadius: $theme.borders.radius200,
                  },
                },
              }}
              content={({ close }) => (
                <StatefulMenu
                  overrides={{
                    Option: {
                      props: {
                        getItemLabel: (item: { label: string }) => (
                          <Row $gap={$theme.sizing.scale300}>
                            <LanguageIcon language={item.label} />
                            {languageLabel(item.label)}
                          </Row>
                        ),
                      },
                      style: {
                        ...$theme.typography.MonoLabelXSmall,
                      },
                    },
                  }}
                  onItemSelect={({ item }) => {
                    setSelectedLanguage(item.label);
                    close();
                  }}
                  items={menuItems}
                />
              )}
              accessibilityType="menu"
            >
              <Button
                kind={ButtonKind.minimal}
                size={SIZE.mini}
                endEnhancer={<ChangeLanguageIcon $style={{ marginLeft: "-4px" }} />}
                overrides={{
                  BaseButton: {
                    style: ({ $theme }) => ({
                      color: $theme.colors.primary300,
                      ...$theme.typography.MonoLabelXSmall,
                      ":hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }),
                  },
                }}
              >
                {languageLabel(selectedLanguage)}
              </Button>
            </StatefulPopover>
          ) : (
            <LanguageIndicator color={$theme.colors.primary300}>{languageLabel(selectedLanguage)}</LanguageIndicator>
          )}
          <CopyButton onClick={copyCode} />
        </CodeBlockTitleBar>
        {Object.entries(languageCodeSnippets).map(([language, data]) => {
          let code;
          let variables;
          if (isString(data)) {
            code = data;
          } else {
            code = data.query;
            variables = data.variables;
          }

          return (
            <div key={language} className={css({ display: selectedLanguage == language ? "auto" : "none", overflow: "auto" })}>
              {props.showJsPlaygroundLink && isJavaScriptLanguage(language) && (
                <OpenInJavaScriptPlaygroundButton
                  code={code}
                  tabName={props.title?.toString() ?? "JavaScript Example"}
                  primaryDomain={currentApp.productionRenderURL}
                  environment={currentApp.environmentName ?? DevelopmentEnvironmentName}
                  $style={{
                    float: "right",
                    marginRight: $theme.sizing.scale500,
                  }}
                />
              )}
              {props.showGqlPlaygroundLink && language == "graphql" && (
                <OpenInGraphQLPlaygroundButton
                  graphql={code}
                  variables={variables}
                  tabName={props.title?.toString() ?? "GraphQL Example"}
                  endpoint={currentApp.productionGraphQLEndpoint}
                  environment={currentApp.environmentName ?? DevelopmentEnvironmentName}
                  $style={{
                    float: "right",
                    marginRight: $theme.sizing.scale500,
                  }}
                />
              )}
              <div
                onClick={copyCode}
                className={css({
                  paddingLeft: $theme.sizing.scale400,
                  paddingRight: $theme.sizing.scale400,
                  paddingBottom: $theme.sizing.scale600,
                })}
              >
                <CodeHighlight language={language}>{code}</CodeHighlight>
              </div>
              {variables && (
                <CodeBlock title="Variables" language="json">
                  {prettifyCodeExample(JSON.stringify(variables), "json", 85)}
                </CodeBlock>
              )}
            </div>
          );
        })}
      </CodeBlockContainer>
    );
  }
);
